import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Textarea, Button, Tooltip, Chip } from '@heroui/react';
import { PaperClipIcon, StopIcon, WrenchIcon, Cog6ToothIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
export default function ChatInput({ onSendMessage, isLoading = false, onStopGeneration, placeholder = "메시지를 입력하세요...", onOpenSettings, isCompact = false, tools = [], selectedTools: externalSelectedTools, onToolSelect: externalOnToolSelect, onToolRemove: externalOnToolRemove, showToolsButton = true, toolsDropdownComponent: ToolsDropdown, showSettingsButton = true, helperText = "AI는 실수할 수 있습니다. 중요한 정보를 확인하세요." }) {
    const [message, setMessage] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [internalSelectedTools, setInternalSelectedTools] = useState([]);
    const [showToolsDropdown, setShowToolsDropdown] = useState(false);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    // Use external or internal selected tools
    const selectedTools = externalSelectedTools || internalSelectedTools;
    const setSelectedTools = externalSelectedTools ? ()=>{} : setInternalSelectedTools;
    // Auto-resize textarea
    const adjustTextareaHeight = useCallback(()=>{
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 200);
        textarea.style.height = `${newHeight}px`;
    }, []);
    useEffect(()=>{
        adjustTextareaHeight();
    }, [
        message,
        adjustTextareaHeight
    ]);
    const handleSubmit = (e)=>{
        e?.preventDefault();
        if (!message.trim() || isLoading) return;
        onSendMessage(message.trim(), attachedFiles.length > 0 ? attachedFiles : undefined, selectedTools.length > 0 ? selectedTools : undefined);
        setMessage('');
        setAttachedFiles([]);
        if (!externalSelectedTools) {
            setInternalSelectedTools([]);
        }
    };
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSubmit();
        }
    };
    const handleFileAttach = ()=>{
        fileInputRef.current?.click();
    };
    const handleFileChange = (e)=>{
        const files = Array.from(e.target.files || []);
        setAttachedFiles((prev)=>[
                ...prev,
                ...files
            ]);
    };
    const removeFile = (index)=>{
        setAttachedFiles((prev)=>prev.filter((_, i)=>i !== index));
    };
    const handleToolSelect = (tool)=>{
        if (externalOnToolSelect) {
            externalOnToolSelect(tool);
        } else {
            setInternalSelectedTools((prev)=>{
                const exists = prev.find((t)=>t.name === tool.name);
                if (exists) {
                    return prev.filter((t)=>t.name !== tool.name);
                } else {
                    return [
                        ...prev,
                        tool
                    ];
                }
            });
        }
    };
    const removeSelectedTool = (toolName)=>{
        if (externalOnToolRemove) {
            externalOnToolRemove(toolName);
        } else {
            setInternalSelectedTools((prev)=>prev.filter((t)=>t.name !== toolName));
        }
    };
    const canSend = message.trim().length > 0 && !isLoading;
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative w-full",
        "data-testid": "ai-chat-input",
        children: [
            /*#__PURE__*/ _jsx("input", {
                ref: fileInputRef,
                type: "file",
                hidden: true,
                multiple: true,
                accept: "image/*,.pdf,.txt,.doc,.docx,.md",
                onChange: handleFileChange
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: `mx-auto w-full ${isCompact ? 'max-w-none' : 'max-w-3xl'}`,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex flex-col rounded-2xl border border-gray-200 shadow-sm transition-colors focus-within:border-gray-300 dark:border-gray-800 dark:focus-within:border-gray-700 bg-transparent",
                        children: [
                            (attachedFiles.length > 0 || selectedTools.length > 0) && /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap gap-2 p-3 pb-0",
                                children: [
                                    attachedFiles.map((file, index)=>/*#__PURE__*/ _jsx(Chip, {
                                            onClose: ()=>removeFile(index),
                                            variant: "flat",
                                            size: "sm",
                                            startContent: /*#__PURE__*/ _jsx(PaperClipIcon, {
                                                className: "w-3 h-3"
                                            }),
                                            children: file.name
                                        }, `file-${index}`)),
                                    selectedTools.map((tool)=>/*#__PURE__*/ _jsx(Chip, {
                                            onClose: ()=>removeSelectedTool(tool.name),
                                            variant: "flat",
                                            color: "primary",
                                            size: "sm",
                                            startContent: /*#__PURE__*/ _jsx(WrenchIcon, {
                                                className: "w-3 h-3"
                                            }),
                                            children: tool.name
                                        }, `tool-${tool.name}`))
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "p-3",
                                children: /*#__PURE__*/ _jsx(Textarea, {
                                    ref: textareaRef,
                                    value: message,
                                    onChange: (e)=>setMessage(e.target.value),
                                    onKeyDown: handleKeyDown,
                                    placeholder: placeholder,
                                    "data-testid": "ai-chat-textarea",
                                    minRows: 1,
                                    maxRows: 6,
                                    classNames: {
                                        base: "w-full",
                                        inputWrapper: "shadow-none border-none p-0 min-h-[40px] bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent",
                                        input: "resize-none py-2 bg-transparent"
                                    },
                                    variant: "flat",
                                    radius: "none"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between border-t border-gray-200 px-3 py-2 dark:border-gray-800",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(Tooltip, {
                                                content: "파일 첨부",
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    isIconOnly: true,
                                                    size: "sm",
                                                    variant: "light",
                                                    onPress: handleFileAttach,
                                                    className: "min-w-unit-8 w-unit-8 h-unit-8",
                                                    children: /*#__PURE__*/ _jsx(PaperClipIcon, {
                                                        className: "w-4 h-4"
                                                    })
                                                })
                                            }),
                                            showToolsButton && tools.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                                className: "relative",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Tooltip, {
                                                        content: "도구 선택",
                                                        children: /*#__PURE__*/ _jsxs(Button, {
                                                            isIconOnly: true,
                                                            size: "sm",
                                                            variant: selectedTools.length > 0 ? "flat" : "light",
                                                            color: tools.length > 0 ? "primary" : "default",
                                                            isDisabled: tools.length === 0,
                                                            onPress: ()=>setShowToolsDropdown(!showToolsDropdown),
                                                            className: "min-w-unit-8 w-unit-8 h-unit-8 relative",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(WrenchIcon, {
                                                                    className: "w-4 h-4"
                                                                }),
                                                                selectedTools.length > 0 && /*#__PURE__*/ _jsx("span", {
                                                                    className: "absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-primary text-[10px] text-white",
                                                                    children: selectedTools.length
                                                                })
                                                            ]
                                                        })
                                                    }),
                                                    showToolsDropdown && tools.length > 0 && ToolsDropdown && /*#__PURE__*/ _jsx("div", {
                                                        className: "absolute bottom-full left-0 mb-2 z-50",
                                                        children: /*#__PURE__*/ _jsx(ToolsDropdown, {
                                                            tools: tools,
                                                            selectedTools: selectedTools,
                                                            onToolSelect: handleToolSelect,
                                                            onClose: ()=>setShowToolsDropdown(false)
                                                        })
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1",
                                        children: [
                                            showSettingsButton && onOpenSettings && /*#__PURE__*/ _jsx(Tooltip, {
                                                content: "설정",
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    isIconOnly: true,
                                                    size: "sm",
                                                    variant: "light",
                                                    onPress: onOpenSettings,
                                                    className: "min-w-unit-8 w-unit-8 h-unit-8",
                                                    children: /*#__PURE__*/ _jsx(Cog6ToothIcon, {
                                                        className: "w-4 h-4"
                                                    })
                                                })
                                            }),
                                            isLoading ? /*#__PURE__*/ _jsx(Tooltip, {
                                                content: "중지",
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    isIconOnly: true,
                                                    size: "sm",
                                                    color: "danger",
                                                    variant: "flat",
                                                    onPress: onStopGeneration,
                                                    className: "min-w-unit-8 w-unit-8 h-unit-8",
                                                    children: /*#__PURE__*/ _jsx(StopIcon, {
                                                        className: "w-4 h-4"
                                                    })
                                                })
                                            }) : /*#__PURE__*/ _jsx(Tooltip, {
                                                content: "전송",
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    isIconOnly: true,
                                                    size: "sm",
                                                    color: "primary",
                                                    isDisabled: !canSend,
                                                    onPress: ()=>handleSubmit(),
                                                    "data-testid": "ai-chat-send-btn",
                                                    className: "min-w-unit-8 w-unit-8 h-unit-8",
                                                    children: /*#__PURE__*/ _jsx(ArrowUpIcon, {
                                                        className: "w-4 h-4"
                                                    })
                                                })
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    helperText && /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-center text-xs text-gray-500 dark:text-gray-400",
                        children: helperText
                    })
                ]
            })
        ]
    });
}
